import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { fetchAllDataAPI } from '../utils/api';
import withAuth from '../utils/withAuth';

ChartJS.register(...registerables);

const BarChartProductOrder = () => {
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [totalOrders, setTotalOrders] = useState(0);

    useEffect(() => {
        getLastSixMonthsChartData();
    }, []);

    // ✅ fallback palette
    const palette = useMemo(
        () => [
            '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
            '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab'
        ],
        []
    );

    const groupPalette = useMemo(
        () => ({
            Delivered: '#59a14f',
            'In Transit': '#4e79a7',
            RTO: '#f28e2b',
            Cancelled: '#e15759',
            Returned: '#af7aa1',
            'Failed Delivery': '#9c755f',
            'Lost / Damaged': '#333333',
            'Hold / Exception': '#edc949',
            'Pending / New': '#76b7b2',
            'All Orders': '#bab0ab',
            Other: '#bab0ab',
        }),
        []
    );

    const getColor = (i) => palette[i % palette.length];

    // ✅ Map raw courier statuses to short groups
    const normalizeStatus = (label = '') => String(label || '').trim().toLowerCase();

    const getShortGroup = (rawLabel) => {
        const s = normalizeStatus(rawLabel);

        if (!s) return 'Other';

        // Delivered
        if (
            s === 'delivered' ||
            s.includes('rto_delivered') ||
            s.includes('delivered back to shipper') ||
            s.includes('reached back at seller city')
        ) return 'Delivered';

        // In Transit
        if (
            s.includes('in transit') ||
            s.includes('out for delivery') ||
            s.includes('ot:rto in transit') ||
            s.includes('shipment inscan') ||
            s.includes('await delivery information') ||
            s.includes('intransit')
        ) return 'In Transit';

        // RTO
        if (
            (s.includes('rto') && !s.includes('rto_delivered')) ||
            s.includes('rto initiated') ||
            s.includes('in rto/rts process') ||
            s.includes('returned to origin initiated') ||
            s.includes("returned to origin at shipper's request") ||
            s.includes('dispatched - dispatched for rto')
        ) return 'RTO';

        // Cancelled
        if (s.includes('cancelled') || s.includes('canceled') || s.includes('shipment cancelled'))
            return 'Cancelled';

        // Returned
        if (s === 'returned' || s.includes('returned back') || s.includes('returned to client'))
            return 'Returned';

        // Failed delivery
        if (
            s.includes('failed attempt') ||
            s.includes('delivery attempted') ||
            s.includes('premises closed') ||
            s.includes('pickup failed')
        ) return 'Failed Delivery';

        // Lost / Damaged
        if (
            s.includes('lost') ||
            s.includes('destroyed') ||
            s.includes('prohibited')
        ) return 'Lost / Damaged';

        // Hold / Exception
        if (
            s.includes('hold') ||
            s.includes('exception') ||
            s.includes('incorrect waybill') ||
            s.includes('address incorrect') ||
            s.includes('consignee address incorrect') ||
            s.includes('necessary charges') ||
            s.includes('delivery delayed')
        ) return 'Hold / Exception';

        // Pending / New
        if (
            s.includes('pending') ||
            s.includes('new') ||
            s.includes('placed') ||
            s.includes('confirmed') ||
            s.includes('contact customer service')
        ) return 'Pending / New';

        return 'Other';
    };

    // ✅ Ensure Chart.js never receives null/invalid colors or non-numeric data
    const normalizeDatasets = (datasets = []) => {
        return datasets.map((ds, i) => {
            const baseColor =
                typeof ds?.backgroundColor === 'string' && ds.backgroundColor
                    ? ds.backgroundColor
                    : getColor(i);

            const cleanArrayColor = (val, fallback) => {
                if (Array.isArray(val)) return val.map((v) => (typeof v === 'string' && v ? v : fallback));
                return typeof val === 'string' && val ? val : fallback;
            };

            const cleanData = Array.isArray(ds?.data)
                ? ds.data.map((v) => {
                    const n = Number(v);
                    return Number.isFinite(n) ? n : 0;
                })
                : [];

            return {
                ...ds,
                data: cleanData,
                backgroundColor: cleanArrayColor(ds?.backgroundColor, baseColor),
                borderColor: cleanArrayColor(ds?.borderColor, baseColor),
                hoverBackgroundColor: cleanArrayColor(ds?.hoverBackgroundColor, baseColor),
                hoverBorderColor: cleanArrayColor(ds?.hoverBorderColor, baseColor),
                borderWidth: ds?.borderWidth ?? 1,
            };
        });
    };

    // ✅ Convert many statuses into short grouped datasets (9–10 max)
    const groupDatasetsToShortStatus = (labels, datasets) => {
        // keep All Orders separately if it exists
        const allOrdersDs = datasets.find((d) => normalizeStatus(d?.label) === 'all orders') || null;

        const groupsOrder = [
            'Delivered',
            'In Transit',
            'RTO',
            'Cancelled',
            'Returned',
            'Failed Delivery',
            'Lost / Damaged',
            'Hold / Exception',
            'Pending / New',
            'Other',
        ];

        const groupedMap = {};
        groupsOrder.forEach((g) => (groupedMap[g] = new Array(labels.length).fill(0)));

        datasets.forEach((ds) => {
            const label = ds?.label || '';
            if (normalizeStatus(label) === 'all orders') return;

            const group = getShortGroup(label);
            const arr = Array.isArray(ds?.data) ? ds.data : [];

            for (let i = 0; i < labels.length; i++) {
                groupedMap[group][i] += Number(arr[i] || 0);
            }
        });

        const groupedDatasets = groupsOrder
            .filter((g) => groupedMap[g].some((v) => Number(v) > 0))
            .map((g, idx) => ({
                label: g,
                data: groupedMap[g],
                backgroundColor: groupPalette[g] || getColor(idx),
                borderColor: groupPalette[g] || getColor(idx),
                borderWidth: 1,
            }));

        // optional: add All Orders at end (if you want)
        if (allOrdersDs) {
            groupedDatasets.push({
                label: 'All Orders',
                data: Array.isArray(allOrdersDs.data) ? allOrdersDs.data : new Array(labels.length).fill(0),
                backgroundColor: groupPalette['All Orders'],
                borderColor: groupPalette['All Orders'],
                borderWidth: 1,
            });
        }

        return groupedDatasets;
    };

    const getLastSixMonthsChartData = async () => {
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI('super-admin-get-last-six-months-data-for-chart');

            if (res?.data?.status === 1) {
                const labels = Array.isArray(res?.data?.chartdata?.labels) ? res.data.chartdata.labels : [];
                const datasetsRaw = Array.isArray(res?.data?.chartdata?.datasets) ? res.data.chartdata.datasets : [];

                // 1) sanitize incoming datasets (fix null colors + NaN)
                const datasetsSanitized = normalizeDatasets(datasetsRaw);

                // 2) group to short statuses
                const datasetsGrouped = groupDatasetsToShortStatus(labels, datasetsSanitized);

                // total (excluding All Orders)
                let total = 0;
                datasetsGrouped.forEach((dataset) => {
                    if (normalizeStatus(dataset.label) !== 'all orders') {
                        total += (dataset.data || []).reduce((sum, value) => sum + (Number(value) || 0), 0);
                    }
                });
                setTotalOrders(total);

                setChartData({ labels, datasets: datasetsGrouped });
            } else {
                console.error('Error fetching chart data:', res?.data?.msg);
                setChartData(null);
            }
        } catch (error) {
            console.error('Error fetching chart data:', error);
            setChartData(null);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white rounded shadow">
            {isLoading ? (
                <p>Loading...</p>
            ) : chartData ? (
                <Bar
                    data={chartData}
                    options={{
                        responsive: true,
                        plugins: {
                            legend: { position: 'top' },
                            title: {
                                display: true,
                                text: `Total Orders: ${totalOrders}`,
                                font: { size: 18, weight: 'bold' },
                                color: '#333333',
                            },
                        },
                        scales: {
                            x: {
                                stacked: false,           // ✅ grouped bars
                                grid: { display: false }, // optional (clean look)
                            },
                            y: {
                                stacked: false,           // ✅ grouped bars
                                beginAtZero: true,
                                grid: { display: true },  // horizontal lines
                            },
                        },
                    }}
                />
            ) : (
                <p>No data available.</p>
            )}
        </div>
    );
};

export default withAuth(BarChartProductOrder);
